import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Modal, Popover, Space, Spin, Typography, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCreateSuspect, useDeleteSuspect, useGetSuspects, useUpdateSuspect } from './hooks';
import { useDebounceFn } from 'ahooks';

// ── Highlight matching text ──────────────────────────────────────────────────
function HighlightText({ text, keyword }) {
  if (!keyword) return <span>{text}</span>;

  const idx = text.toLowerCase().indexOf(keyword.toLowerCase());
  if (idx === -1) return <span>{text}</span>;

  return (
    <span>
      {text.slice(0, idx)}
      <mark style={{ backgroundColor: '#faad14', color: '#000', padding: 0, borderRadius: 2 }}>
        {text.slice(idx, idx + keyword.length)}
      </mark>
      {text.slice(idx + keyword.length)}
    </span>
  );
}

// ── Inline row: view / edit / delete ────────────────────────────────────────
function SuspectRow({ item, keyword, isFirstMatch, rowRef, onSelect }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.name);

  const { mutate: update, isLoading: updating } = useUpdateSuspect({
    id: item._id,
    onSuccess: () => setEditing(false),
  });

  const { mutate: remove, isLoading: deleting } = useDeleteSuspect({ id: item._id });

  const handleDelete = (e) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'ยืนยันการลบ',
      content: `ต้องการลบ "${item.name}" ออกจากระบบ?`,
      okText: 'ยืนยัน',
      okType: 'danger',
      cancelText: 'ยกเลิก',
      onOk: () => remove(),
    });
  };

  if (editing) {
    return (
      <Space size={4} style={{ width: '100%' }}>
        <Input
          size="small"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onPressEnter={() => update({ payload: { name: editValue } })}
          style={{ flex: 1 }}
          autoFocus
        />
        <Button
          type="text"
          size="small"
          icon={<CheckOutlined style={{ color: '#52c41a' }} />}
          loading={updating}
          onClick={() => update({ payload: { name: editValue } })}
        />
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined style={{ color: '#aaa' }} />}
          onClick={() => {
            setEditValue(item.name);
            setEditing(false);
          }}
        />
      </Space>
    );
  }

  return (
    <div
      ref={isFirstMatch ? rowRef : null}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        padding: '2px 4px',
        borderRadius: 4,
        backgroundColor: isFirstMatch && keyword ? 'rgba(250,173,20,0.08)' : 'transparent',
      }}
    >
      {/* left: click to select */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }} onClick={() => onSelect?.(item.name)}>
        <UserOutlined style={{ opacity: 0.5 }} />
        <Typography.Text style={{ fontSize: 13 }}>
          <HighlightText text={item.name} keyword={keyword} />
        </Typography.Text>
      </div>
      {/* right: edit / delete */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined style={{ opacity: 0.7 }} />}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
        />
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined style={{ color: '#f5222d', opacity: 0.8 }} />}
          loading={deleting}
          onClick={handleDelete}
        />
      </div>
    </div>
  );
}

// ── Popover content ──────────────────────────────────────────────────────────
function SuspectListPopover({ keyword, onSelect, onAdd, creating }) {
  const listRef = useRef(null);
  const firstMatchRef = useRef(null);

  const { data: allSuspects = [], isLoading } = useGetSuspects();
  const suspects = useMemo(() => (Array.isArray(allSuspects) ? allSuspects : []), [allSuspects]);

  const isDuplicate = useMemo(() => {
    if (!keyword) return false;
    return suspects.some((s) => s.name?.trim().toLowerCase() === keyword.trim().toLowerCase());
  }, [keyword, suspects]);

  // index of first match for scroll target
  const firstMatchIndex = useMemo(() => {
    if (!keyword) return -1;
    return suspects.findIndex((s) => s.name?.toLowerCase().includes(keyword.toLowerCase()));
  }, [keyword, suspects]);

  // scroll to first match whenever keyword changes
  useEffect(() => {
    if (firstMatchRef.current && listRef.current) {
      firstMatchRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [keyword]);

  return (
    <div style={{ width: 350 }}>
      {isDuplicate && (
        <Typography.Text type="danger" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
          ชื่อนี้มีอยู่ในระบบแล้ว
        </Typography.Text>
      )}

      {keyword && !isDuplicate && (
        <Button size="small" type="dashed" block loading={creating} style={{ marginBottom: 8 }} onClick={onAdd}>
          {`เพิ่ม "${keyword}"`}
        </Button>
      )}

      <Spin spinning={isLoading} indicator={<LoadingOutlined />}>
        <div
          ref={listRef}
          style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {suspects.length === 0 && !isLoading && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, textAlign: 'center', display: 'block', padding: '8px 0' }}
            >
              ยังไม่มีรายการ
            </Typography.Text>
          )}
          {suspects.map((item, index) => (
            <SuspectRow
              key={item._id}
              item={item}
              keyword={keyword}
              isFirstMatch={index === firstMatchIndex}
              rowRef={firstMatchRef}
              onSelect={onSelect}
            />
          ))}
        </div>
      </Spin>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export function SpSuspectField({
  placeholder = '',
  required = false,
  inputProps = undefined,
  rules = [],
  disabled,
  form,
  // injected by Form.Item
  value: formValue,
  onChange: formOnChange,
  ...props
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formValue || '');
  const [debouncedValue, setDebouncedValue] = useState('');
  const leaveTimer = useRef(null);

  // sync inputValue when form resets or sets value externally
  useEffect(() => {
    setInputValue(formValue || '');
  }, [formValue]);

  const { run: debounceSearch } = useDebounceFn((val) => setDebouncedValue(val), { wait: 400 });

  const notifyForm = (name) => {
    formOnChange?.(name);
    if (form && props.name) {
      form.setFieldValue(props.name, name);

      props?.onTrigger && props.onTrigger();
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    debounceSearch(val);
    notifyForm(val);
    if (!popoverOpen) setPopoverOpen(true);
  };

  const { data: allSuspects = [] } = useGetSuspects();

  const { mutate: create, isLoading: creating } = useCreateSuspect({
    onSuccess: (res) => {
      setDebouncedValue('');
      const createdName = res?.name || inputValue;
      setInputValue(createdName);
      notifyForm(createdName);
      setPopoverOpen(false);
    },
    onError: () => {
      message.error('เกิดข้อผิดพลาดในการเพิ่มผู้ต้องหา');
    },
  });

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    const isDuplicate = (Array.isArray(allSuspects) ? allSuspects : []).some(
      (s) => s.name?.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      message.warning('ชื่อนี้มีอยู่ในระบบแล้ว');
      return;
    }
    create({ payload: { name: trimmed } });
  };

  const handleSelect = (name) => {
    setInputValue(name);
    setDebouncedValue('');
    notifyForm(name);
    setPopoverOpen(false);
  };

  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setPopoverOpen(false), 200);
  };

  const handleMouseEnter = () => clearTimeout(leaveTimer.current);

  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  return (
    <Form.Item
      rules={[{ required, message: `กรุณากรอก${props.label}` }, ...rules]}
      noStyle={inputProps?.type === 'hidden'}
      {...props}
    >
      <Popover
        open={popoverOpen}
        onOpenChange={(open) => setPopoverOpen(open)}
        trigger="focus"
        placement="bottomLeft"
        title={
          <Typography.Text strong style={{ fontSize: 13 }}>
            รายการผู้ต้องหา
          </Typography.Text>
        }
        content={
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <SuspectListPopover
              keyword={debouncedValue}
              onSelect={handleSelect}
              onAdd={handleAdd}
              creating={creating}
            />
          </div>
        }
        destroyTooltipOnHide
      >
        <Input
          {...inputProps}
          value={inputValue}
          placeholder={placeholder || `ค้นหาหรือเพิ่ม${props.label || 'ผู้ต้องหา'}...`}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={() => setPopoverOpen(true)}
          style={{ cursor: 'text', ...inputProps?.style }}
          suffix={creating ? <LoadingOutlined /> : null}
        />
      </Popover>
    </Form.Item>
  );
}
